import {Component, inject, OnDestroy, OnInit, signal, ViewChild} from '@angular/core';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {MatPaginator, PageEvent} from '@angular/material/paginator';
import {MatSort, Sort} from '@angular/material/sort';
import {ActivatedRoute, Router} from '@angular/router';
import {forkJoin, Subject} from 'rxjs';
import {take, takeUntil} from 'rxjs/operators';
import {detailsExpand} from '../../../../../../common';
import {ConfirmationDialogComponent} from '../../../../../../common/dialogs/confirmation-dialog/confirmation-dialog.component';
import {PagedFilterDTONew} from '../../../../../../common/models/PagedFilterDTO';
import {TableControl} from '../../../../../../common/models/TableControl';
import {SharedService} from '../../../../../../common/services/shared-service';
import {UserAuthorisationService} from '../../../../../../common/services/user-authorisation.service';
import {ProductStore} from '../../../../../../common/store/product.store';
import {EntityTable} from '../../../../../../common/types/entity-table.enum';
import {PortalType} from '../../../../../../common/types/portal.enum';
import {DateUtil} from '../../../../../../common/util/DateUtil';
import {OtherRequestBuilder} from '../../../../../../other/services/other-request-builder';
import {InsuredPersonDTO} from '../../../../../models/InsuredPersonDTO';
import {EntRequestBuilder} from '../../../../../services/ent-request-builder';
import {InsuredPersonService} from '../../../../../services/insured-person.service';
import {BrokerDTO} from '../../../../../../onboarding-admin-portal/models/broker/BrokerDTO';
import {getPaginatorLengthFromPagedResponse, getStatusColor} from 'src/app/modules/common/util/Util';

enum TableColumns {
  Icons = 'icons',
  Title = 'title',
  Name = 'name',
  CardNumber = 'cardNumber',
  NationalIdNumber = 'nationalIdNumber',
  Association = 'association',
  BirthDate = 'birthDate',
  Gender = 'gender',
  Product = 'product',
  Status = 'status'
}

@Component({
  selector: 'greydom-persons',
  templateUrl: './persons.component.html',
  animations: [detailsExpand]
})
export class PersonsComponent implements OnInit, OnDestroy {
  readonly getStatusColor = getStatusColor;

  readonly tableColumns = TableColumns;
  readonly destroyed$ = new Subject();
  readonly dateFormat = DateUtil.dateFormat;
  readonly dateTimeFormat = DateUtil.dateTimeFormat;
  readonly PortalType = PortalType;

  insuredPersonDataSource: InsuredPersonDTO[];
  insuredPersonTableControl: TableControl;
  insuredPersonExpandedRow: InsuredPersonDTO | null;
  insuredPersonDisplayedColumns: TableColumns[];
  hasPermissions: boolean;
  hasOnboardingRole = false;
  currentPortal: PortalType;
  products = signal<string[]>(null);

  @ViewChild(MatPaginator, {static: false}) paginator: MatPaginator;
  @ViewChild(MatSort, {static: false}) sort: MatSort;

  private loggedInBrokerCode: string;
  private loggedInBrokerProfile: BrokerDTO;
  private readonly productStore = inject(ProductStore);

  constructor(
    private insuredPersonService: InsuredPersonService,
    private sharedService: SharedService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    public dialog: MatDialog,
    private userAuthorisationService: UserAuthorisationService,
  ) {
    const controls = OtherRequestBuilder.getTableControls();
    this.insuredPersonTableControl = new TableControl(
      controls.length,
      controls.pageSize,
      controls.pageNumber,
      controls.pageSizeOptions,
      'insuredPersonId',
      'desc',
      'lastName',
      ''
    );
    const parentData = this.activatedRoute.parent?.snapshot.data;
    this.currentPortal = parentData?.portalType || this.PortalType.Normal;
    this.hasPermissions = this.userAuthorisationService.hasRoleEnterpriseManager();
    this.insuredPersonDisplayedColumns = Object.values<TableColumns>(TableColumns);
  }

  ngOnInit(): void {
    this.hasOnboardingRole = this.doesUserHaveEditPermissions();
    this.restoreCachedProducts();
    this.getLoggedInBrokerCode();
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  getLoggedInBrokerCode(): void{
    const userProfile$ = this.sharedService.getLoggedInUserProfile$().pipe(
      take(1),
      takeUntil(this.destroyed$)
    );

    const brokerProfile$ = this.sharedService.getLoggedInBrokerProfile$().pipe(
      take(1),
      takeUntil(this.destroyed$)
    );

    forkJoin([userProfile$, brokerProfile$]).subscribe(([userProfile, broker]) => {
      if (broker) {
        this.loggedInBrokerProfile = broker;
      }

      if (userProfile) {
        this.loggedInBrokerCode = this.sharedService.getProfileLinkedEntityCode();
      }

      this.getListOfInsuredPersons(this.insuredPersonTableControl);
    });
  }

  onValueChanged(value: string) {
    this.insuredPersonTableControl.filterValue = value;
    this.getListOfInsuredPersons(this.insuredPersonTableControl);
  }

  onProductChanged(event: string[]): void {
    if (event?.length === 0) {
      return;
    }
    this.paginator?.firstPage();
    this.insuredPersonTableControl.pageNumber = 0;
    this.products.set(event);
    this.productStore.cacheSelectedProducts(event);
    this.getListOfInsuredPersons(this.insuredPersonTableControl);
  }

  onClickAddPerson(): void {
    this.router.navigate([this.getRouteForAddPerson()]);
  }

  onClickEditPerson(event: MouseEvent, id: number): void {
    event.preventDefault();
    event.stopPropagation();
    this.router.navigate([this.getRouteForEditPerson(id)]);
  }

  getRouteForAddPerson(): string {
    const routes = {
      [PortalType.Normal]: '/enterprise-management/add-person',
      [PortalType.OnBoarding]: '/onboarding/add-person',
      [PortalType.Broker]: '/broker/add-person'
    };
    return routes[this.currentPortal] || routes[this.PortalType.Normal];
  }

  getRouteForEditPerson(personId: number): string {
    const routes = {
      [this.PortalType.Normal]: `/enterprise-management/edit-person/${personId}`,
      [this.PortalType.OnBoarding]: `/onboarding/edit-person/${personId}`,
      [this.PortalType.Broker]: `/broker/edit-person/${personId}`
    };
    return routes[this.currentPortal] || routes[this.PortalType.Normal];
  }

  onClickDeletePerson(event: MouseEvent, id: number) {
    event.preventDefault();
    event.stopPropagation();

    const dialogRef: MatDialogRef<ConfirmationDialogComponent> = this.dialog.open(ConfirmationDialogComponent, {
      width: '40vw',
      data: `Are you sure you wish to delete this Person with ID ${id}?`
    });

    dialogRef
      .afterClosed()
      .subscribe((isConfirmed) => {
        if (isConfirmed) {
          this.insuredPersonService.deleteInsuredPerson(id)
            .pipe(take(1))
            .subscribe({
              next: () => {
                this.sharedService.openSnackBar(`Person ${id} deleted`, 'Dismiss');
                this.getListOfInsuredPersons(this.insuredPersonTableControl);
              },
              error: (errorResponse) => {
                const error = errorResponse.error;
                this.sharedService.openErrorDialog(error.businessMessage);
              }
            });
        }
      });
  }

  sortData($event: Sort): void {
    if (!$event.direction) {
      return;
    }
    if (this.paginator != null) {
      this.paginator.firstPage();
    }
    const sortFieldMap = {
      [TableColumns.Title]: 'titleByTitleId.name',
      [TableColumns.Name]: 'lastName',
      [TableColumns.Association]: 'associationByAssociationId.name',
      [TableColumns.BirthDate]: 'dateOfBirth',
      [TableColumns.Gender]: 'genderByGenderId.name',
      [TableColumns.Status]: 'coverStatusName',
      [TableColumns.Product]: 'productName'
    };
    this.insuredPersonTableControl.pageNumber = 0;
    this.insuredPersonTableControl.sortField = sortFieldMap[$event.active] || $event.active;
    this.insuredPersonTableControl.sortDirection = $event.direction;
    this.getListOfInsuredPersons(this.insuredPersonTableControl);
  }

  pageChanged($event: PageEvent) {
    this.insuredPersonTableControl.pageNumber = $event.pageIndex;
    this.insuredPersonTableControl.pageSize = $event.pageSize;
    this.getListOfInsuredPersons(this.insuredPersonTableControl);
  }

  getPersonFullName(person: InsuredPersonDTO) {
    const middleName = person.middleName ? person.middleName : '';
    return `${person.firstName} ${middleName} ${person.lastName}`;
  }

  private getListOfInsuredPersons(tableControl: TableControl): void {
    const pagedRequestDTO = EntRequestBuilder.buildBasicPagedRequestDTO(EntityTable.TblInsuredPerson, tableControl);

    pagedRequestDTO.fieldMap = {};

    if (this.currentPortal === PortalType.Broker && !this.loggedInBrokerCode) {
      return; // Imp!! Don't fetch persons other than that broker
    }

    // Add broker filter for broker portal to only show persons for current broker
    if (this.currentPortal === PortalType.Broker) {
      const loggedInAsBrokerGroup = this.loggedInBrokerProfile != null
        && this.loggedInBrokerProfile.businessType?.value == 7
        && this.loggedInBrokerProfile.brokerAgents != null
        && this.loggedInBrokerProfile.brokerAgents.length > 0;

      if (loggedInAsBrokerGroup) {
        const brokerFilterGroup = new PagedFilterDTONew();
        brokerFilterGroup.name = 'brokerNumber';
        brokerFilterGroup.operator = 'in';
        brokerFilterGroup.value = this.loggedInBrokerProfile.brokerAgents.map(agent => agent.code).join(','); // Concatenate all broker agents' codes
        brokerFilterGroup.bracket = '(';
        pagedRequestDTO.fieldMap['brokerNumber_1'] = brokerFilterGroup;
      }

      const brokerFilter = new PagedFilterDTONew();
      brokerFilter.name = 'brokerNumber';
      brokerFilter.operator = '==';
      brokerFilter.value = this.loggedInBrokerCode; // current broker username
      if (loggedInAsBrokerGroup) {
        brokerFilter.logicGate = 'OR';
        brokerFilter.bracket = ')';
      }
      pagedRequestDTO.fieldMap['brokerNumber'] = brokerFilter;
    }

    const productFilter = this.productStore.buildProductFilter(this.products(), 'productCode');
    if (productFilter) {
      pagedRequestDTO.fieldMap['productCode'] = productFilter;
    }

    const keyCardNumber = 'cardNumber';
    const pagedFilterCardNumberDTO = new PagedFilterDTONew();
    pagedFilterCardNumberDTO.name = `${keyCardNumber}`;
    pagedFilterCardNumberDTO.value = tableControl.filterValue;
    pagedFilterCardNumberDTO.bracket = '(';
    pagedRequestDTO.fieldMap[keyCardNumber] = pagedFilterCardNumberDTO;

    const keyLastName = 'lastName';
    const pagedFilterLastNameDTO = new PagedFilterDTONew();
    pagedFilterLastNameDTO.name = `${keyLastName}`;
    pagedFilterLastNameDTO.value = tableControl.filterValue;
    pagedFilterLastNameDTO.logicGate = 'OR';
    pagedFilterLastNameDTO.bracket = '';
    pagedRequestDTO.fieldMap[keyLastName] = pagedFilterLastNameDTO;

    const keyIdNumber = 'nationalIdNumber';
    const pagedFilterIdNumberDTO = new PagedFilterDTONew();
    pagedFilterIdNumberDTO.name = `${keyIdNumber}`;
    pagedFilterIdNumberDTO.value = tableControl.filterValue;
    pagedFilterIdNumberDTO.logicGate = 'OR';
    pagedFilterIdNumberDTO.bracket = ')';
    pagedRequestDTO.fieldMap[keyIdNumber] = pagedFilterIdNumberDTO;

    this.insuredPersonService.getListOfInsuredPersons(pagedRequestDTO).subscribe({
      next: (response) => {
        this.insuredPersonDataSource = (response.resultList as InsuredPersonDTO[]);
        this.insuredPersonTableControl.length = getPaginatorLengthFromPagedResponse(
          response,
          tableControl.pageNumber,
          tableControl.pageSize
        );
      },
      error: (errorResponse) => {
        const error = errorResponse.error;
        this.sharedService.openErrorDialog(error.businessMessage);
      }
    });
  }

  private restoreCachedProducts(): void {
    const cached = this.productStore.cachedSelectedProducts();
    if (cached?.length > 0) {
      this.products.set(cached);
    }
  }

  private doesUserHaveEditPermissions(): boolean {
   return this.userAuthorisationService.hasRoleEnterpriseManager() || this.userAuthorisationService.hasOnBoardingTeamLeader();
  }

}
